"use client";

/**
 * One Concepts/Guides/Venture Graph nav link, shared by every render context —
 * `LearnHeader` and both the desktop and mobile chrome of `LandingNav` — so
 * click tracking and the active/`aria-current` logic exist in exactly one
 * place. A prior version duplicated this across all three (Copilot review,
 * PR #860): the click tracking existed on none of them, which is exactly how
 * three copies of the same behavior drift. Each caller still supplies its own
 * `className` — the contexts legitimately look different (the full-width bar vs.
 * the floating pill, desktop vs. mobile sizing) — but none of them re-implement
 * the link itself.
 *
 * A client LEAF, not a client boundary: `LearnHeader` renders this inside
 * pages that must stay statically prerendered (see `LearnPageChrome.tsx`'s
 * docblock). A "use client" leaf is safe there — it still prerenders to HTML
 * and hydrates, so a crawler still gets the link — as long as it reads no
 * request state, which this doesn't; see `learn.prerender.test.ts`'s
 * `CLIENT_LEAVES_ON_PRERENDERED_PAGES` guard, which this file is added to.
 */
import Link from "next/link";

import { trackLandingNavClicked } from "@/lib/analytics/client";
import type { LearnNavSection } from "@/lib/learn/nav";

export interface LearnNavLinkProps {
  section: LearnNavSection;
  href: string;
  label: string;
  /** Which section the current page belongs to, not necessarily this link's own; absent outside the sections. */
  active?: LearnNavSection;
  className: string;
}

export function LearnNavLink({ section, href, label, active, className }: LearnNavLinkProps) {
  return (
    <Link
      href={href}
      aria-current={section === active ? "location" : undefined}
      onClick={() => trackLandingNavClicked({ section })}
      className={className}
    >
      {label}
    </Link>
  );
}
